import { useEffect, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { useAuth } from "@/hooks/useAuth";
import { Loader2, WifiOff, ServerCrash, RefreshCw, X, Building2, Eye, EyeOff, ArrowLeft } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useTenant } from "@/contexts/TenantContext";
import vendasLogo from "@/assets/vendasgroup.png";

export default function Auth() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const {
    signIn,
    signOut,
    user,
    authError,
    authErrorCode,
    isRetryableAuthError,
    retryCount,
    isReconnecting,
    retryNow,
    clearAuthError
  } = useAuth();
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [isLoggingIn, setIsLoggingIn] = useState(false); // Bloqueia useEffect durante login manual
  const [orgInactiveError, setOrgInactiveError] = useState(false);
  const [orgInactiveMessage, setOrgInactiveMessage] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [forgotPasswordMode, setForgotPasswordMode] = useState(false);
  const [resetEmail, setResetEmail] = useState("");
  const [resetEmailSent, setResetEmailSent] = useState(false);
  const { tenant, isLoading: tenantLoading } = useTenant();
  const [isDarkMode, setIsDarkMode] = useState(false);

  // Detectar mudanças de tema (dark/light)
  useEffect(() => {
    const checkDarkMode = () => {
      setIsDarkMode(document.documentElement.classList.contains("dark"));
    };
    checkDarkMode();
    
    const observer = new MutationObserver(checkDarkMode);
    observer.observe(document.documentElement, { 
      attributes: true, 
      attributeFilter: ["class"] 
    });
    
    return () => observer.disconnect();
  }, []);

  // Verificar se veio de org desativada
  useEffect(() => {
    if (searchParams.get("reason") === "org_inactive") {
      setOrgInactiveError(true);
      setOrgInactiveMessage("Sua organização está temporariamente desativada. Entre em contato com o responsável pela sua empresa para reativar o acesso.");
    }
  }, [searchParams]);

  // Estado de offline
  const [isOffline, setIsOffline] = useState(typeof navigator !== "undefined" ? navigator.onLine === false : false);

  // Detectar mudanças de conectividade
  useEffect(() => {
    const onOnline = () => {
      setIsOffline(false);
      if (authErrorCode === "OFFLINE" || authErrorCode === "NETWORK") {
        clearAuthError();
      }
    };
    const onOffline = () => setIsOffline(true);
    window.addEventListener("online", onOnline);
    window.addEventListener("offline", onOffline);
    return () => {
      window.removeEventListener("online", onOnline);
      window.removeEventListener("offline", onOffline);
    };
  }, [authErrorCode, clearAuthError]);

  // Redirect if already logged in (e não tem erro de org inativa)
  // IMPORTANTE: Não redirecionar se isLoggingIn = true (handleLogin vai fazer o redirect correto)
  useEffect(() => {
    if (user && !orgInactiveError && !isLoggingIn) {
      // Verificar se é partner antes de redirecionar
      const checkAndRedirect = async () => {
        const { data: partnerMember } = await supabase
          .from('partner_members')
          .select('role')
          .eq('user_id', user.id)
          .maybeSingle();
        
        const isPartner = partnerMember?.role && ['owner', 'admin', 'support'].includes(partnerMember.role);
        
        if (isPartner) {
          navigate("/partner", { replace: true });
        } else {
          navigate("/dashboard", { replace: true });
        }
      };
      checkAndRedirect();
    }
  }, [user, navigate, orgInactiveError, isLoggingIn]);

  // Escolher logo baseado no tema (dark/light)
  const getThemeLogo = () => {
    if (!tenant) return null;
    
    const branding = tenant.branding;
    
    if (isDarkMode) {
      return branding?.logo_dark_url || branding?.logo_light_url || tenant.logoUrl;
    }
    return branding?.logo_light_url || tenant.logoUrl;
  };

  // Determinar logo e textos baseado no tenant
  const logoUrl = getThemeLogo() || vendasLogo;
  const systemName = tenant?.branding?.system_name || "Vendas.Group";
  const loginTitle = tenant?.branding?.login_title;
  const loginSubtitle = tenant?.branding?.login_subtitle || "Plataforma completa para gestão e crescimento de vendas";

  // Verificar status da org ANTES de tentar login (via edge function)
  const checkOrgStatus = async (email: string): Promise<{ canLogin: boolean; reason: string | null; message?: string }> => {
    try {
      const response = await supabase.functions.invoke("check-org-status", {
        body: { email }
      });

      if (response.error) {
        console.error("Erro ao verificar status da org:", response.error);
        // Em caso de erro, permitir tentativa de login (fail-open)
        return { canLogin: true, reason: null };
      }

      return response.data || { canLogin: true, reason: null };
    } catch (err) {
      console.error("Erro ao chamar check-org-status:", err);
      return { canLogin: true, reason: null };
    }
  };

  const handleLogin = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setLoading(true);
    setIsLoggingIn(true); // Bloquear useEffect de redirecionar durante login
    setOrgInactiveError(false);
    setOrgInactiveMessage("");

    const formData = new FormData(e.currentTarget);
    const email = (formData.get("email") as string)?.trim().toLowerCase();
    const password = formData.get("password") as string;

    if (!email || !password) {
      toast({
        title: "Campos obrigatórios",
        description: "Preencha email e senha",
        variant: "destructive"
      });
      setLoading(false);
      setIsLoggingIn(false);
      return;
    }

    // PASSO 1: Verificar se a org está ativa ANTES de tentar login
    const orgStatus = await checkOrgStatus(email);
    
    if (!orgStatus.canLogin && orgStatus.reason === "org_inactive") {
      setOrgInactiveError(true);
      setOrgInactiveMessage(orgStatus.message || "Sua organização está temporariamente desativada. Entre em contato com o responsável pela sua empresa para reativar o acesso.");
      setLoading(false);
      setIsLoggingIn(false);
      return; // NÃO tenta autenticar
    }

    // PASSO 2: Só tenta login se passou na verificação
    const { error, data } = await signIn(email, password);
    
    if (!error && data?.user) {
      // Salvar welcome message no sessionStorage para exibir no destino
      const welcomeMsg = tenant?.branding?.welcome_message;
      if (welcomeMsg) {
        sessionStorage.setItem('partner_welcome_message', welcomeMsg);
      }

      // Verificar se é partner member (qualquer role)
      const { data: partnerMember } = await supabase
        .from('partner_members')
        .select('role')
        .eq('user_id', data.user.id)
        .maybeSingle();

      // Todos os partner members (owner, admin, support) vão para /partner
      const isPartner = partnerMember?.role && ['owner', 'admin', 'support'].includes(partnerMember.role);

      if (isPartner) {
        navigate("/partner", { replace: true });
      } else {
        navigate("/dashboard", { replace: true });
      }
    }
    setLoading(false);
    setIsLoggingIn(false);
  };

  const handleRetry = async () => {
    setLoading(true);
    const result = await retryNow();
    if (!result.error && result.data?.user) {
      const { data: partnerMember } = await supabase
        .from('partner_members')
        .select('role')
        .eq('user_id', result.data.user.id)
        .maybeSingle();

      // Todos os partner members (owner, admin, support) vão para /partner
      const isPartner = partnerMember?.role && ['owner', 'admin', 'support'].includes(partnerMember.role);
      if (isPartner) {
        navigate("/partner", { replace: true });
      } else {
        navigate("/dashboard", { replace: true });
      }
    }
    setLoading(false);
  };

  // Função para enviar email de recuperação de senha
  const handleForgotPassword = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setLoading(true);

    const email = resetEmail.trim().toLowerCase();
    if (!email) {
      toast({
        title: "Email obrigatório",
        description: "Digite seu email para recuperar a senha",
        variant: "destructive"
      });
      setLoading(false);
      return;
    }

    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/reset-password`
    });

    if (error) {
      toast({
        title: "Erro ao enviar email",
        description: error.message,
        variant: "destructive"
      });
    } else {
      setResetEmailSent(true);
      toast({
        title: "Email enviado!",
        description: "Verifique sua caixa de entrada para redefinir sua senha."
      });
    }
    setLoading(false);
  };

  // Determinar se deve mostrar banner de infraestrutura
  const showInfraBanner = isOffline || authErrorCode === "OFFLINE" || authErrorCode === "NETWORK" || authErrorCode === "SERVICE_UNAVAILABLE" || authErrorCode === "TIMEOUT" || authErrorCode === "CIRCUIT_OPEN" || authErrorCode === "AUTH_UNAVAILABLE" || (authError && isRetryableAuthError);
  const isOfflineError = isOffline || authErrorCode === "OFFLINE";

  // Background customizado do login (tenant/parceiro)
  const loginBgUrl = tenant?.branding?.login_background_url;

  return (
    <div
      className="min-h-screen flex items-center justify-center bg-gradient-to-br from-background via-background to-muted p-4"
      style={{
        backgroundImage: loginBgUrl ? `url(${loginBgUrl})` : undefined,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
      }}
    >
      {/* Overlay para legibilidade quando tem background image */}
      {loginBgUrl && (
        <div className="fixed inset-0 bg-black/50 z-0" />
      )}
      <Card className={`w-full max-w-md ${loginBgUrl ? 'relative z-10' : ''}`}>
        <CardHeader className="space-y-1 text-center">
          {tenantLoading ? (
            <div className="flex justify-center py-4">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : (
            <div className="flex justify-center mb-4">
              <img
                src={logoUrl}
                alt={systemName}
                className="h-24 sm:h-28 md:h-40 lg:h-48 w-auto max-w-full object-contain rounded-lg"
              />
            </div>
          )}
          {loginTitle && loginTitle !== systemName && (
            <CardTitle className="text-xl">{loginTitle}</CardTitle>
          )}
          <CardDescription>
            {loginSubtitle}
          </CardDescription>
        </CardHeader>

        <CardContent className="space-y-4">
          {/* Banner de organização desativada - MENSAGEM ESPECÍFICA */}
          {orgInactiveError && (
            <Alert variant="destructive" className="relative border-destructive/50 bg-destructive/10">
              <div className="flex items-start gap-3">
                <Building2 className="h-5 w-5 mt-0.5 flex-shrink-0 text-destructive" />
                <div className="flex-1 space-y-1">
                  <AlertTitle className="text-sm font-semibold text-destructive">
                    Acesso bloqueado
                  </AlertTitle>
                  <AlertDescription className="text-sm text-destructive/90">
                    {orgInactiveMessage || "Sua organização está temporariamente desativada."}
                    <br />
                    <strong>Entre em contato com o responsável pela sua empresa</strong> para reativar o acesso.
                  </AlertDescription>
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-6 w-6 flex-shrink-0 text-destructive hover:text-destructive/80"
                  onClick={() => {
                    setOrgInactiveError(false);
                    setOrgInactiveMessage("");
                    navigate("/auth", { replace: true });
                  }}
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            </Alert>
          )}
          
          {/* Banner de erro de infraestrutura */}
          {showInfraBanner && !orgInactiveError && (
            <Alert variant="destructive" className="relative">
              <div className="flex items-start gap-3">
                {isOfflineError ? (
                  <WifiOff className="h-5 w-5 mt-0.5 flex-shrink-0" />
                ) : (
                  <ServerCrash className="h-5 w-5 mt-0.5 flex-shrink-0" />
                )}
                <div className="flex-1 space-y-2">
                  <AlertTitle className="text-sm font-semibold">
                    {isOfflineError ? "Você está sem internet" : "Servidor temporariamente indisponível"}
                  </AlertTitle>
                  <AlertDescription className="text-sm">
                    {isOfflineError 
                      ? "Assim que a conexão voltar, você poderá tentar novamente." 
                      : authError ?? "Estamos tentando reconectar automaticamente."}
                  </AlertDescription>
                  
                  {!isOfflineError && isRetryableAuthError && (
                    <div className="text-xs text-muted-foreground mt-1">
                      {isReconnecting ? (
                        <span className="flex items-center gap-1">
                          <Loader2 className="h-3 w-3 animate-spin" />
                          Reconectando…
                        </span>
                      ) : (
                        <span>Você pode tentar novamente agora.</span>
                      )}
                      {retryCount > 0 && <span className="ml-2">Tentativas: {retryCount}</span>}
                    </div>
                  )}

                  <div className="flex flex-wrap gap-2 mt-3">
                    {isRetryableAuthError && !isReconnecting && (
                      <Button 
                        size="sm" 
                        variant="outline" 
                        onClick={handleRetry} 
                        disabled={loading || isReconnecting} 
                        className="gap-1"
                      >
                        <RefreshCw className="h-3 w-3" />
                        Tentar novamente
                      </Button>
                    )}
                    <Button size="sm" variant="ghost" asChild>
                      <a href="https://status.supabase.com/" target="_blank" rel="noopener noreferrer">
                        Ver status
                      </a>
                    </Button>
                    <Button size="sm" variant="ghost" onClick={clearAuthError} className="ml-auto">
                      <X className="h-3 w-3" />
                    </Button>
                  </div>
                </div>
              </div>
            </Alert>
          )}

          {/* Erro de credenciais */}
          {authError && authErrorCode === "BAD_CREDENTIALS" && !orgInactiveError && (
            <Alert variant="destructive">
              <AlertTitle className="text-sm font-semibold">
                Credenciais inválidas
              </AlertTitle>
              <AlertDescription className="text-sm">
                E-mail ou senha incorretos. Verifique e tente novamente.
              </AlertDescription>
            </Alert>
          )}

          {/* Form de recuperação de senha */}
          {forgotPasswordMode ? (
            <div className="space-y-4">
              {resetEmailSent ? (
                <div className="text-center space-y-4">
                  <div className="mx-auto w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center">
                    <svg className="w-6 h-6 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                  </div>
                  <div>
                    <h3 className="font-semibold text-foreground">Email enviado!</h3>
                    <p className="text-sm text-muted-foreground mt-1">
                      Verifique sua caixa de entrada em <strong>{resetEmail}</strong> e clique no link para redefinir sua senha.
                    </p>
                  </div>
                  <Button
                    variant="outline"
                    className="w-full"
                    onClick={() => {
                      setForgotPasswordMode(false);
                      setResetEmailSent(false);
                      setResetEmail("");
                    }}
                  >
                    <ArrowLeft className="mr-2 h-4 w-4" />
                    Voltar para o login
                  </Button>
                </div>
              ) : (
                <form onSubmit={handleForgotPassword} className="space-y-4">
                  <div className="text-center mb-4">
                    <h3 className="font-semibold text-foreground">Recuperar Senha</h3>
                    <p className="text-sm text-muted-foreground mt-1">
                      Digite seu email para receber o link de recuperação.
                    </p>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="reset-email">Email</Label>
                    <Input
                      id="reset-email"
                      type="email"
                      placeholder="seu@email.com"
                      value={resetEmail}
                      onChange={(e) => setResetEmail(e.target.value)}
                      required
                      disabled={loading}
                    />
                  </div>
                  <Button
                    type="submit"
                    className="w-full"
                    disabled={loading || isOffline}
                  >
                    {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                    {loading ? "Enviando..." : "Enviar Link"}
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    className="w-full"
                    onClick={() => {
                      setForgotPasswordMode(false);
                      setResetEmail("");
                    }}
                  >
                    <ArrowLeft className="mr-2 h-4 w-4" />
                    Voltar para o login
                  </Button>
                </form>
              )}
            </div>
          ) : (
            <>
              {/* Form de login */}
              <form onSubmit={handleLogin} className="space-y-4">
                <div className="space-y-2">
              <Label htmlFor="login-email">Email</Label>
              <Input 
                id="login-email" 
                name="email" 
                type="email" 
                placeholder="seu@email.com" 
                required 
                disabled={loading || isReconnecting} 
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="login-password">Senha</Label>
              <div className="relative">
                <Input 
                  id="login-password" 
                  name="password" 
                  type={showPassword ? "text" : "password"} 
                  placeholder="••••••••" 
                  required 
                  disabled={loading || isReconnecting}
                  className="pr-10"
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                  onClick={() => setShowPassword(!showPassword)}
                  tabIndex={-1}
                >
                  {showPassword ? (
                    <EyeOff className="h-4 w-4 text-muted-foreground" />
                  ) : (
                    <Eye className="h-4 w-4 text-muted-foreground" />
                  )}
                </Button>
              </div>
            </div>
            
            <div className="text-right">
              <button
                type="button"
                onClick={() => {
                  setForgotPasswordMode(true);
                  setResetEmailSent(false);
                }}
                className="text-sm text-primary hover:underline"
              >
                Esqueci minha senha
              </button>
            </div>
            
            <Button 
              type="submit" 
              className="w-full" 
              disabled={loading || isReconnecting || isOffline}
            >
              {(loading || isReconnecting) && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {isReconnecting ? "Reconectando…" : loading ? "Entrando…" : "Entrar"}
            </Button>
          </form>
          
          <div className="mt-4 text-center text-sm text-muted-foreground">
            Não tem acesso? Entre em contato com o administrador.
          </div>
          
          <div className="mt-2 text-center text-xs text-muted-foreground">
            <Link 
              to="/politicasdeprivacidade" 
              className="hover:text-foreground hover:underline transition-colors"
            >
              Política de Privacidade
            </Link>
            <span className="mx-2">•</span>
            <Link 
              to="/termos" 
              className="hover:text-foreground hover:underline transition-colors"
            >
              Termos de Uso
            </Link>
          </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
